"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { ArrowRight, Check, Sparkle } from "@/components/ui/icons";
import { INTELLIGENCE } from "@/content/quikit";

type State = "idle" | "loading" | "done";
const EASE_IN_OUT = [0.77, 0, 0.175, 1] as const;

function Spinner() {
  return (
    <span className="relative block size-4 animate-[spin_0.9s_steps(8)_infinite]" aria-hidden="true">
      {Array.from({ length: 8 }).map((_, i) => (
        <span key={i} className="absolute left-1/2 top-0 h-full w-[3px]" style={{ transform: `translateX(-50%) rotate(${i * 45}deg)` }}>
          <span className="block size-[3px] rounded-full bg-ink" style={{ opacity: 0.2 + (i / 8) * 0.8 }} />
        </span>
      ))}
    </span>
  );
}

/**
 * "See it in action": three signals from three apps land one after another, the arrow
 * draws, and Quik Intelligence's recommendation arrives. Its action button morphs
 * pill → spinner → confirmed (auto-plays once in view, then replays on click).
 */
export function IntelligenceFlow() {
  const root = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>("idle");
  const timers = useRef<number[]>([]);
  const played = useRef(false);
  const busy = useRef(false);

  const run = useCallback(() => {
    if (busy.current) return;
    busy.current = true;
    played.current = true;
    setState("loading");
    timers.current.push(
      window.setTimeout(() => setState("done"), 1400),
      window.setTimeout(() => {
        setState("idle");
        busy.current = false;
      }, 4800),
    );
  }, []);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      const q = (s: string) => gsap.utils.toArray<HTMLElement>(s, el);
      gsap
        .timeline({ scrollTrigger: { trigger: el, start: "top 78%", toggleActions: PLAY_ONCE } })
        .fromTo(el, { opacity: 0, y: 60, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 1.3, ease: "silk" })
        .fromTo(q("[data-flow-src]"), { opacity: 0, y: 20, scale: 0.94 }, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "silk", stagger: 0.22 }, 0.35)
        .fromTo(q("[data-flow-plus]"), { opacity: 0, scale: 0.4 }, { opacity: 1, scale: 1, duration: 0.5, ease: "back.out(2)", stagger: 0.22 }, 0.6)
        .fromTo(q("[data-flow-arrow]"), { scaleX: 0, transformOrigin: "0% 50%" }, { scaleX: 1, duration: 0.8, ease: "glide" }, 1.2)
        .fromTo(q("[data-flow-out]"), { opacity: 0, x: 30, filter: "blur(8px)" }, { opacity: 1, x: 0, filter: "blur(0px)", duration: 1, ease: "silk", clearProps: "filter" }, 1.6)
        .fromTo(q("[data-flow-foot]"), { opacity: 0 }, { opacity: 1, duration: 0.8 }, 2)
        .call(() => {
          timers.current.push(window.setTimeout(() => !played.current && run(), 700));
        });
    },
    { scope: root },
  );

  const label = state === "done" ? "Save-play assigned" : "Assign save-play";

  return (
    <div
      ref={root}
      data-reveal-init=""
      className="rounded-[24px] border border-white/[0.07] bg-[linear-gradient(180deg,#171718_0%,#101011_100%)] p-5 md:p-8"
    >
      <p className="text-[11.5px] font-medium uppercase tracking-[0.12em] text-white/40">See it in action</p>
      <div className="mt-6 flex flex-col items-stretch gap-3 lg:flex-row lg:items-center">
        {INTELLIGENCE.sources.map(([app, text], i) => (
          <div key={app} className="contents">
            <div data-flow-src="" className="flex-1 rounded-[16px] bg-white/[0.05] p-4 ring-1 ring-inset ring-white/[0.06]">
              <p className="text-[11px] text-white/45">{app}</p>
              <p className="mt-1 text-[14px] font-medium text-white">{text}</p>
            </div>
            {i < INTELLIGENCE.sources.length - 1 && (
              <span data-flow-plus="" className="grid size-7 shrink-0 place-items-center self-center rounded-full bg-white/[0.07] text-[15px] text-white/60">
                +
              </span>
            )}
          </div>
        ))}
        <span data-flow-arrow="" className="hidden h-px w-10 shrink-0 bg-mint/60 lg:block" />
        <ArrowRight className="mx-auto size-5 rotate-90 text-mint lg:hidden" />
        <div data-flow-out="" className="flex-[1.35] rounded-[16px] bg-mint/[0.09] p-4 ring-1 ring-inset ring-mint/25">
          <p className="flex items-center gap-1.5 text-[11px] text-mint">
            <Sparkle className="size-3.5" /> {INTELLIGENCE.output.app}
          </p>
          <p className="mt-1 text-[14px] font-medium text-white">Churn risk flagged.</p>
          <motion.button
            type="button"
            onClick={run}
            aria-live="polite"
            initial={false}
            animate={{ width: state === "loading" ? 36 : "auto", backgroundColor: state === "done" ? "#2de2d0" : "#ffffff" }}
            transition={{ width: { duration: 0.5, ease: EASE_IN_OUT }, backgroundColor: { duration: 0.35 } }}
            className="mt-3 flex h-9 items-center justify-center overflow-hidden rounded-full px-4 text-[14px] font-medium text-ink"
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {state === "loading" ? (
                <motion.span key="spin" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                  <Spinner />
                </motion.span>
              ) : (
                <motion.span
                  key={label}
                  initial={{ opacity: 0, filter: "blur(4px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, filter: "blur(4px)" }}
                  className="flex items-center gap-1.5 whitespace-nowrap"
                >
                  {state === "done" && <Check className="size-3.5" strokeWidth={2.4} />}
                  {label}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>
      <p data-flow-foot="" className="mt-6 text-[14px] text-white/50">
        {INTELLIGENCE.foot}
      </p>
    </div>
  );
}
